import { ImageOff, MoveDiagonal } from 'lucide-react'
import { useRef, useState, type KeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react'

import type { SceneryCandidate, SceneryItem } from '@/api/stages'
import { cn } from '@/components/ui/utils'

import { byZ, itemThumb, moveItem, removeItem, resizeItem, shiftZ, updateItem } from './scenery'
import { BAND_LABEL } from './words'

/**
 * The layout editor's picture: the background with every piece of scenery drawn where it stands
 * (its feet at `x`/`y`, `height_pct` tall, back to front by `z`). Drag a thing to move it, drag
 * the round handle above a selected thing to make it bigger or smaller, click the picture to let
 * go. Keyboard: arrows move (Shift = further), `+`/`-` resize, `F` flips, `[`/`]` send back /
 * bring forward, Delete removes.
 *
 * Moves during a drag go through `onLive` (no undo step); `onGestureStart`/`onGestureEnd` bracket
 * the drag so it is ONE undo step. Keyboard edits are whole steps (`onChange`).
 */

export interface SceneryCanvasProps {
  picture: string
  items: readonly SceneryItem[]
  selectedId: string | null
  candidates?: ReadonlyMap<string, SceneryCandidate>
  onSelect(id: string | null): void
  onGestureStart(): void
  onLive(items: SceneryItem[]): void
  onGestureEnd(): void
  onChange(items: SceneryItem[]): void
  className?: string
}

/** Pointer moves smaller than this (percent of the stage) are a click, not a drag. */
const DRAG_THRESHOLD_PCT = 0.4

interface Gesture {
  kind: 'move' | 'resize'
  id: string
  pointerId: number
  startX: number
  startY: number
  items: readonly SceneryItem[]
  item: SceneryItem
  moved: boolean
}

export function SceneryCanvas({ picture, items, selectedId, candidates, onSelect, onGestureStart, onLive, onGestureEnd, onChange, className }: SceneryCanvasProps) {
  const box = useRef<HTMLDivElement | null>(null)
  const gesture = useRef<Gesture | null>(null)
  const [pictureBroken, setPictureBroken] = useState(false)

  const rect = () => {
    const r = box.current?.getBoundingClientRect()
    return { left: r?.left ?? 0, top: r?.top ?? 0, width: r?.width || 1, height: r?.height || 1 }
  }

  const start = (kind: Gesture['kind'], item: SceneryItem, e: ReactPointerEvent<HTMLElement>) => {
    if (e.button !== 0) return
    e.stopPropagation()
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId)
    } catch {
      /* a synthetic pointer cannot be captured; the drag still works */
    }
    // Keyboard nudges work right after a click.
    if (kind === 'move') e.currentTarget.focus({ preventScroll: true })
    onSelect(item.id)
    gesture.current = { kind, id: item.id, pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, items, item, moved: false }
    onGestureStart()
  }

  const move = (e: ReactPointerEvent<HTMLElement>) => {
    const g = gesture.current
    if (!g || g.pointerId !== e.pointerId) return
    const r = rect()
    const dx = ((e.clientX - g.startX) / r.width) * 100
    const dy = ((e.clientY - g.startY) / r.height) * 100
    if (!g.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PCT) return
    g.moved = true
    if (g.kind === 'move') {
      onLive(moveItem(g.items, g.id, g.item.x + dx, g.item.y + dy))
    } else {
      // The feet stay put: the new height is from the feet up to the pointer.
      const pointerY = ((e.clientY - r.top) / r.height) * 100
      onLive(resizeItem(g.items, g.id, g.item.y - pointerY))
    }
  }

  const finish = (e: ReactPointerEvent<HTMLElement>) => {
    const g = gesture.current
    if (!g || g.pointerId !== e.pointerId) return
    gesture.current = null
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId)
    } catch {
      /* not captured */
    }
    onGestureEnd()
  }

  const onKey = (item: SceneryItem, e: KeyboardEvent<HTMLElement>) => {
    const step = e.shiftKey ? 5 : 1
    let next: SceneryItem[] | null = null
    switch (e.key) {
      case 'ArrowLeft':
        next = moveItem(items, item.id, item.x - step, item.y)
        break
      case 'ArrowRight':
        next = moveItem(items, item.id, item.x + step, item.y)
        break
      case 'ArrowUp':
        next = moveItem(items, item.id, item.x, item.y - step)
        break
      case 'ArrowDown':
        next = moveItem(items, item.id, item.x, item.y + step)
        break
      case '+':
      case '=':
        next = resizeItem(items, item.id, item.height_pct + step)
        break
      case '-':
      case '_':
        next = resizeItem(items, item.id, item.height_pct - step)
        break
      case 'f':
      case 'F':
        next = updateItem(items, item.id, { flip: !item.flip })
        break
      case ']':
        next = shiftZ(items, item.id, 1)
        break
      case '[':
        next = shiftZ(items, item.id, -1)
        break
      case 'Delete':
      case 'Backspace':
        next = removeItem(items, item.id)
        onSelect(null)
        break
      case 'Escape':
        onSelect(null)
        ;(e.currentTarget as HTMLElement).blur()
        e.preventDefault()
        return
      default:
        return
    }
    e.preventDefault()
    if (next) onChange(next)
  }

  return (
    <div
      ref={box}
      className={cn('relative aspect-video w-full touch-none select-none overflow-hidden rounded-xl bg-paper-3', className)}
      onPointerDown={() => onSelect(null)}
      data-testid="scenery-canvas"
      role="group"
      aria-label="The background with its scenery. Drag things to move them."
    >
      {pictureBroken ? (
        <div className="absolute inset-0 grid place-items-center text-ink-3">
          <ImageOff className="size-8" aria-hidden />
        </div>
      ) : (
        <img src={picture} alt="" draggable={false} onError={() => setPictureBroken(true)} className="pointer-events-none absolute inset-0 size-full object-cover" />
      )}

      {byZ(items).map((it, rank) => {
        const selected = it.id === selectedId
        const thumb = itemThumb(it, candidates)
        return (
          <div
            key={it.id}
            role="button"
            tabIndex={0}
            aria-pressed={selected}
            aria-label={`${it.name}, ${BAND_LABEL[it.band].toLowerCase()}`}
            data-testid="scenery-item"
            data-id={it.id}
            className={cn(
              'absolute flex cursor-grab items-end justify-center rounded-md outline-none active:cursor-grabbing',
              'focus-visible:ring-2 focus-visible:ring-accent',
              selected ? 'ring-2 ring-accent ring-offset-1 ring-offset-transparent' : 'hover:ring-1 hover:ring-white/70',
            )}
            // The drawing order, not `z` itself: a saved `z` may be negative (the sky's default is -2),
            // and a negative z-index would hide the thing behind the picture.
            style={{ left: `${it.x}%`, top: `${it.y}%`, height: `${it.height_pct}%`, transform: 'translate(-50%, -100%)', zIndex: 1 + rank }}
            onPointerDown={(e) => start('move', it, e)}
            onPointerMove={move}
            onPointerUp={finish}
            onPointerCancel={finish}
            onKeyDown={(e) => onKey(it, e)}
            onFocus={() => (selected ? undefined : onSelect(it.id))}
          >
            <SceneryPicture src={thumb} name={it.name} flip={it.flip} />
            {selected ? (
              <span
                aria-hidden
                data-testid="resize-handle"
                title="Drag up or down to change the size"
                className="absolute -right-3 -top-3 grid size-7 cursor-ns-resize place-items-center rounded-full border-2 border-white bg-accent text-accent-ink shadow-card"
                onPointerDown={(e) => start('resize', it, e)}
                onPointerMove={move}
                onPointerUp={finish}
                onPointerCancel={finish}
              >
                <MoveDiagonal className="size-3.5" />
              </span>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

/** The thing's picture at the slot's height; a labelled box when it has none. */
function SceneryPicture({ src, name, flip }: { src: string | null; name: string; flip: boolean }) {
  const [broken, setBroken] = useState(false)
  if (!src || broken) {
    return (
      <span className="flex aspect-[3/5] h-full items-center justify-center rounded-md border border-dashed border-white/80 bg-black/25 p-1 text-center text-[10px] font-medium leading-tight text-white">
        {name}
      </span>
    )
  }
  return (
    <img
      src={src}
      alt=""
      draggable={false}
      onError={() => setBroken(true)}
      className="pointer-events-none block h-full w-auto min-w-4 max-w-none object-contain drop-shadow"
      style={flip ? { transform: 'scaleX(-1)' } : undefined}
    />
  )
}
